import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import { RouteComponentProps, withRouter } from 'react-router-dom';

import { RootState } from '../../../modules';
import {
  getPageMouseStaticsAsync,
  getPageElementDataAsync,
  setTargetElementId,
  GET_EFFECTIVE_CLICK_COUNT,
  GET_PAGE_ATTENTION_SCREENS_COUNT,
  GET_PAGE_DURATION,
  getEffectiveClickCountAsync,
  getPageAttentionScreensCountAsync,
  getPageDurationAsync,
  getPageViewCountAsync,
} from '../../../modules/PageAnalytics/UXAnalytics';

import {
  GetPageMouseStaticsParamType,
  GetPageElementDataParamType,
  GetEffectiveClickCountParamType,
  GetPageAttentionScreensCountParamType,
  GetPageDurationParamType,
  GetPageViewCountParamType,
} from '../../../api/pageAnalytics/uxAnalytics/type';

import { GetPageElementsFlowParamType } from '../../../api/pageAnalytics/uxAnalytics/mouseflow/type';
import { getPageElementsFlowAsync } from '../../../modules/PageAnalytics/UXAnalytics/MouseFlow';
import { GetScrollEventsParamType } from '../../../api/pageAnalytics/uxAnalytics/scroll/type';
import { getScrollEventsAsync } from '../../../modules/PageAnalytics/UXAnalytics/Scroll';

import PageUXAnalysisTemplate from '../../../components/Templates/PageDetailAnalysisTemplates/PageUXAnalysisTemplate';
import { MouseHeatmapTooltipType } from '../../../components/Molecule/Tooltip/MouseHeatmapTooltip';
import { GetPageFlowParamType } from '../../../api/pageAnalytics/userFlowAnalysis/type';
import {
  getPageFlowConversionAsync,
  getPageFlowInflowAsync,
} from '../../../modules/PageAnalytics/UserFlowAnalysis';

const Component = styled.div`
  width: 100%;
  height: 100%;
`;

interface UXAnalysisProps extends RouteComponentProps {}

const UXAnalysis = ({ location }: UXAnalysisProps) => {
  // redux
  const { scrollEventsData } = useSelector((state: RootState) => state.scroll);
  const { mouseflowData } = useSelector((state: RootState) => state.mouseFlow);
  const {
    loadingMouseStatics,
    mouseStaticsData,
    elementData,
    effectiveClickCount,
    pageViewCount,
    pageDuration,
  } = useSelector((state: RootState) => state.pageUXAnalytics);
  const { startDate, endDate, isDatePickerToggle } = useSelector(
    (state: RootState) => state.datePicker
  );
  // 흐름 분석
  const { conversionData, inflowData } = useSelector(
    (state: RootState) => state.userFlowAnalysis
  );

  const loading = useSelector((state: RootState) => state.loading);
  const dispatch = useDispatch();

  const onGetScrollEvents = (info: GetScrollEventsParamType) => {
    dispatch(getScrollEventsAsync.request(info));
  };

  // 유효클릭 수
  const onGetEffectiveClickRate = (info: GetEffectiveClickCountParamType) => {
    dispatch(getEffectiveClickCountAsync.request(info));
  };

  // 유효클릭 수
  const onGetPageViewCount = (info: GetPageViewCountParamType) => {
    dispatch(getPageViewCountAsync.request(info));
  };

  const onGetPageAttentionScreensCount = (
    info: GetPageAttentionScreensCountParamType
  ) => {
    dispatch(getPageAttentionScreensCountAsync.request(info));
  };

  // 페이지 체류시간
  const onGetPageDuration = (info: GetPageDurationParamType) => {
    dispatch(getPageDurationAsync.request(info));
  };

  const onGetPageClickStatics = (info: GetPageMouseStaticsParamType) => {
    dispatch(getPageMouseStaticsAsync.request(info));
  };

  const onGetPageElementData = (info: GetPageElementDataParamType) => {
    dispatch(getPageElementDataAsync.request(info));
  };

  const onSetTargetElementId = (targetElementId: string) => {
    dispatch(setTargetElementId(targetElementId));
  };

  const onGetPageElementsFlow = (info: GetPageElementsFlowParamType) => {
    dispatch(getPageElementsFlowAsync.request(info));
  };

  // 흐름 분석
  const onGetPageFlowInflow = (info: GetPageFlowParamType) => {
    dispatch(getPageFlowInflowAsync.request(info));
  };
  const onGetPageFlowConversion = (info: GetPageFlowParamType) => {
    dispatch(getPageFlowConversionAsync.request(info));
  };

  // state
  const [pageId, setPageId] = useState('');
  const [pageName, setPageName] = useState('');
  const [pageURL, setPageURL] = useState('');

  const [activeDataType, setActiveDataType] = useState(0);

  const [clickedElementPath, setClickedElementPath] = useState('');
  const [mouseoverElementPath, setMouseoverElementPath] = useState('');

  const [clickedPlusElementPath, setClickedPlusElementPath] = useState('');

  const [clickTooltipInfo, setClickTooltipInfo] =
    useState<MouseHeatmapTooltipType | null>(null);
  const [mouseoverTooltipInfo, setMouseoverTooltipInfo] =
    useState<MouseHeatmapTooltipType | null>(null);

  useEffect(() => {
    onSetTargetElementId('');
    const params: { [key: string]: string } = {};
    const arrLocation = location.search.split('&pagelocation=');
    location.search.replace(
      /[?&]+([^=&]+)=([^&]*)/gi,
      (str: string, key: string, value: string) => {
        params[key] = value;
        return value;
      }
    );

    setPageId(params.pageid);
    setPageName(params.pagename);
    setPageURL(arrLocation[arrLocation.length - 1]);
  }, []);

  useEffect(() => {
    if (!isDatePickerToggle && pageId !== '') {
      onGetEffectiveClickRate({
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        page_id: pageId,
      });
      onGetPageViewCount({
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        page_id: pageId,
      });
      onGetPageAttentionScreensCount({
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        page_id: pageId,
      });
      onGetPageDuration({
        start_date: moment(startDate).format('YYYY-MM-DD'),
        end_date: moment(endDate).format('YYYY-MM-DD'),
        page_id: pageId,
      });
      onSetTargetElementId('');
    }
  }, [isDatePickerToggle, startDate, endDate, pageId]);

  useEffect(() => {
    if (!isDatePickerToggle && pageId !== '') {
      if (activeDataType === 0) {
        onGetPageElementData({
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          page_id: pageId,
          event_status: 'click',
        });
        onGetPageClickStatics({
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          page_id: pageId,
          event_status: 'click',
        });
      } else if (activeDataType === 1) {
        onGetScrollEvents({
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          page_id: pageId,
        });
      } else if (activeDataType === 2) {
        onGetPageFlowInflow({
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          page_location: pageURL,
          position: 'previous',
        });
        onGetPageFlowConversion({
          start_date: moment(startDate).format('YYYY-MM-DD'),
          end_date: moment(endDate).format('YYYY-MM-DD'),
          page_location: pageURL,
          position: 'next',
        });
      }
      onSetTargetElementId('');
    }
  }, [isDatePickerToggle, startDate, endDate, pageId, activeDataType]);

  return (
    <Component>
      <PageUXAnalysisTemplate
        headerTitle="페이지 UX 분석"
        pageId={pageId}
        pageTitle={pageName}
        pageURL={pageURL}
        loadingStatics={loadingMouseStatics}
        pageEventStaticsData={mouseStaticsData}
        pageElementData={elementData}
        clickedElementPath={clickedElementPath}
        mouseoverElementPath={mouseoverElementPath}
        clickTooltipInfo={clickTooltipInfo}
        flowData={inflowData}
        conversionData={conversionData}
        mouseoverTooltipInfo={mouseoverTooltipInfo}
        pageUsabilityData={{
          pageViewCount,
          effectiveClickCount,
          pageDuration,
        }}
        pageUsabilityIsLoading={
          loading[GET_EFFECTIVE_CLICK_COUNT] ||
          loading[GET_PAGE_ATTENTION_SCREENS_COUNT] ||
          loading[GET_PAGE_DURATION] ||
          false
        }
        activeDataType={activeDataType}
        mouseflowData={mouseflowData}
        clickedPlusElementPath={clickedPlusElementPath}
        scrollEventsData={scrollEventsData}
        loading={loading}
        setClickedElementPath={setClickedElementPath}
        setMouseoverElementPath={setMouseoverElementPath}
        setClickTooltipInfo={setClickTooltipInfo}
        setMouseoverTooltipInfo={setMouseoverTooltipInfo}
        setActiveDataType={setActiveDataType}
        setClickedPlusElementPath={setClickedPlusElementPath}
      />
    </Component>
  );
};

export default withRouter(UXAnalysis);
